import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { clearAuthStorage, parseJwt, refreshToken } from 'apollo-client'
import { useAuth } from 'react-oidc-context'
import { clearAlertQueue, setAlertQueue } from 'cache/alerts'
import { getIsDealLocked } from 'cache/deal-status'
import { getUserActivity } from 'cache/user-activity'
import errorMessages from 'constants/errorMessages'
import { useUnlockDeal } from 'hooks/use-deal-unlock'
import { MessageType } from 'ts/enums/messageType.enums'

interface TokenExpiryContextProps {
  setTokenExpiryTimeout: (token: string) => void
  setDealNumber: (dealNumber: number) => void
  setDealId: (dealId: number | null) => void
  setUserId: (userId: string) => void
  handleExpiredTokenWarning: () => void
  dealNumber: number | null
  dealId: number | null
  userId: string | null
}

const TokenExpiryContext = createContext<TokenExpiryContextProps | null>(null)

interface TokenExpiryProviderProps {
  children: ReactNode
}

export const TokenExpiryProvider: React.FC<TokenExpiryProviderProps> = ({ children }) => {
  const timeoutId = useRef<NodeJS.Timeout | null>(null)
  const { unlockDealMutation } = useUnlockDeal()
  const { removeUser, signinRedirect } = useAuth()
  const [dealNumber, setDealNumber] = useState<number | null>(null)
  // Initialise to null; the useEffect below restores from sessionStorage on mount.
  // Keeping the storage read out of useState avoids a silent 0 → null coercion and
  // makes React state the unambiguous source of truth during the component's lifetime.
  const [dealId, setDealId] = useState<number | null>(null)
  const [userId, setUserId] = useState<string | null>(localStorage.getItem('userId') ?? null)

  // On mount, restore the tab's active dealId from sessionStorage so that page refreshes
  // don't lose track of the deal that was open in this tab.
  useEffect(() => {
    const stored = Number(sessionStorage.getItem('dealId'))
    if (stored) {
      setDealId(stored)
    }
  }, [])

  // Function to set dealId in sessionStorage (tab-specific, prevents cross-tab pollution).
  const setDealIdState = useCallback((id: number | null) => {
    if (id === null) {
      sessionStorage.removeItem('dealId')
    } else {
      sessionStorage.setItem('dealId', id.toString())
    }
    setDealId(id)
  }, []) // setDealId is a stable useState setter

  // Function to set userId and store in localStorage
  const setUserIdState = useCallback((id: string | null) => {
    if (id === null) {
      localStorage.removeItem('userId')
    } else {
      localStorage.setItem('userId', id.toString())
    }
    setUserId(id)
  }, []) // setUserId is a stable useState setter

  const clearExistingTimers = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
      timeoutId.current = null
    }
  }, []) // timeoutId is a ref — always stable

  // Ref used only for the recursive re-arm call inside the setTimeout callback,
  // avoiding a circular dependency in useCallback's dependency array.
  const setTokenExpiryTimeoutRef = useRef<(token: string) => void>(null!)

  const handleOnAlertClose = useCallback(() => {
    clearAlertQueue()
    // Persist the current path so SuccessSSO can redirect back after re-login.
    const returnPath = window.location.pathname + window.location.search
    localStorage.setItem('frompath', returnPath)
    removeUser()
    signinRedirect()
  }, [removeUser, signinRedirect])

  const handleExpiredTokenWarning = useCallback(async () => {
    setAlertQueue({
      type: MessageType.WARNING,
      message: errorMessages.SESSION_EXPIRED,
      onClose: handleOnAlertClose,
      onOk: handleOnAlertClose,
    })
    // Clear auth storage to prevent any further API calls with the expired token while the user is deciding to re-login.
    localStorage.removeItem('user_logout')
    localStorage.removeItem('user_login')
    clearAuthStorage()
  }, [handleOnAlertClose])

  const handleUnlockDealsBeforeTokenExp = useCallback(async () => {
    // sessionStorage is tab-specific — reads the dealId opened in THIS tab.
    const dealId = Number(sessionStorage.getItem('dealId')) || 0
    const userId = localStorage.getItem('userId') ?? ''

    if (getIsDealLocked() && dealId && userId) {
      try {
        await unlockDealMutation(dealId, userId)
      } catch (error) {
        console.error('Failed to unlock deal on token expiry:', error)
      }
    }
    handleExpiredTokenWarning()
  }, [unlockDealMutation, handleExpiredTokenWarning])

  const setTokenExpiryTimeout = useCallback(
    (token: string) => {
      const payload = parseJwt(token)
      if (!payload || typeof payload.exp !== 'number') return

      const expiryTime = payload.exp * 1000 // Convert expiry to milliseconds
      const bufferTime = 30000 // 30 seconds in milliseconds
      const HALF_HOUR_IN_MS = 30 * 60 * 1000 // 30 minutes in milliseconds
      const currentTime = Date.now() // Returns the number of milliseconds elapsed since midnight, January 1, 1970 UTC
      // The time (in milliseconds) remaining before the token expiry warning is shown.
      // It is computed by subtracting the current time and a buffer time from the token expiry time.
      const alertTime = expiryTime - currentTime - bufferTime

      localStorage.setItem('tokenExpiryTime', expiryTime.toString())
      localStorage.setItem('alertTime', alertTime.toString())

      clearExistingTimers()

      timeoutId.current = setTimeout(async () => {
        const userLastActiveTime = getUserActivity()
        const userInactiveTime = Date.now() - userLastActiveTime

        if (userInactiveTime < HALF_HOUR_IN_MS) {
          try {
            const token = await refreshToken()
            localStorage.setItem('user_logout', 'false')
            token && setTokenExpiryTimeoutRef.current(token)
          } catch (error) {
            console.error('Token refresh failed:', error)
            // Multi-tab race condition: another tab may have already refreshed the token,
            // invalidating this tab's refresh token. If the user is still active, swallow
            // the error and let the next scheduled refresh attempt succeed naturally.
          }
        } else {
          console.error(
            'Session Expired due to User inactive for ' + userInactiveTime / 60000 + ' minutes'
          )
          localStorage.setItem('user_logout', 'true')
          handleUnlockDealsBeforeTokenExp()
        }
      }, alertTime) as unknown as NodeJS.Timeout // TBA Use timeRemaining-bufferTime when tkn exp time config from auth server
    },
    [clearExistingTimers, handleUnlockDealsBeforeTokenExp]
  )

  useEffect(() => {
    // Keep the ref current so the recursive call inside setTimeout always uses
    // the latest memoized function reference.
    setTokenExpiryTimeoutRef.current = setTokenExpiryTimeout

    // On initial load / page refresh, an existing token won't trigger a 'user_login'
    // storage event, so kick off the expiry timeout here if a token is already present.
    const token = localStorage.getItem('token') ?? ''
    if (token) {
      setTokenExpiryTimeoutRef.current(token)
    }
  }, [setTokenExpiryTimeout])

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'user_login') {
        localStorage.setItem('user_logout', 'false')
        const token = localStorage.getItem('token') ?? ''
        token && setTokenExpiryTimeoutRef.current(token)
      }
      if (event.key === 'user_logout' && event.newValue === 'true') {
        clearExistingTimers()
        handleUnlockDealsBeforeTokenExp()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      clearExistingTimers()
    }
  }, [setTokenExpiryTimeout, clearExistingTimers, handleUnlockDealsBeforeTokenExp])

  const contextValue = useMemo(
    () => ({
      setTokenExpiryTimeout,
      setDealNumber,
      setDealId: setDealIdState,
      setUserId: setUserIdState,
      handleExpiredTokenWarning,
      dealNumber,
      dealId,
      userId,
    }),
    [
      dealNumber,
      dealId,
      userId,
      setTokenExpiryTimeout,
      setDealIdState,
      setUserIdState,
      handleExpiredTokenWarning,
    ]
  )

  return <TokenExpiryContext.Provider value={contextValue}>{children}</TokenExpiryContext.Provider>
}

export const useTokenExpiryHandler = () => {
  const context = useContext(TokenExpiryContext)
  if (!context) {
    throw new Error('useTokenExpiryHandler must be used within a TokenExpiryProvider')
  }
  return context
}
